import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { sendPublicReport as sendPublicReportApi } from '../common/api/reports';
import { validateSendSelectedReportsInputs } from './dashboard/SendSelectedReportsModal';
import type { CSSProperties } from 'react';
import type { StatusIndicatorProps } from '@cloudscape-design/components';
import type {
  SendPublicReportRequest,
  SendSelectedReportChannelResult,
  SendSelectedReportsResponse,
} from '../common/types';

const channelDetailStyle: CSSProperties = {
  overflowWrap: 'break-word',
  wordBreak: 'normal',
};

export type PublicScorecardSendModalProps = {
  visible: boolean;
  reportUuid: string;
  reportName: string;
  customerVolume?: number;
  onDismiss: () => void;
  sendReport?: (uuid: string, body: SendPublicReportRequest) => Promise<SendSelectedReportsResponse>;
};

function channelLabel(channel: string): string {
  if (channel === 'sms') return 'SMS';
  if (channel === 'email') return 'Email';
  return 'Scorecard';
}

function statusType(status: string): StatusIndicatorProps.Type {
  if (status === 'success') return 'success';
  if (status === 'forbidden') return 'warning';
  return 'error';
}

function allChannelsSent(result: SendSelectedReportsResponse | null): boolean {
  return Boolean(
    result?.results.length &&
      result.results.every(
        (item) => item.channels.length > 0 && item.channels.every((channel) => channel.status === 'success'),
      ),
  );
}

function hasSuccessfulChannel(result: SendSelectedReportsResponse): boolean {
  return result.results.some((item) => item.channels.some((channel) => channel.status === 'success'));
}

function hasFailedChannel(result: SendSelectedReportsResponse): boolean {
  return result.results.some((item) => item.channels.some((channel) => channel.status !== 'success'));
}

function ResultChannel({ channel }: { channel: SendSelectedReportChannelResult }) {
  return (
    <div style={channelDetailStyle}>
      <StatusIndicator type={statusType(channel.status)}>{channelLabel(channel.channel)}</StatusIndicator>
      {channel.detail ? <span>: {channel.detail}</span> : null}
    </div>
  );
}

export function PublicScorecardSendModal({
  visible,
  reportUuid,
  reportName,
  customerVolume,
  onDismiss,
  sendReport = sendPublicReportApi,
}: PublicScorecardSendModalProps) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ email?: string; phone?: string; form?: string }>({});
  const [result, setResult] = useState<SendSelectedReportsResponse | null>(null);

  useEffect(() => {
    if (!visible) {
      setEmail('');
      setPhone('');
      setErrors({});
      setResult(null);
    }
  }, [visible]);

  const mutation = useMutation({
    mutationFn: (body: SendPublicReportRequest) => sendReport(reportUuid, body),
  });

  const allSent = allChannelsSent(result);
  const partialFailure = Boolean(result && !allSent && hasSuccessfulChannel(result) && hasFailedChannel(result));

  const onSubmit = async () => {
    const nextErrors = validateSendSelectedReportsInputs(email, phone);
    setErrors(nextErrors);
    if (nextErrors.email || nextErrors.phone || nextErrors.form) return;

    const body: SendPublicReportRequest = {
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(phone.trim() ? { phone_number: phone.trim() } : {}),
      ...(customerVolume === undefined ? {} : { customer_volume: customerVolume }),
    };
    try {
      const response = await mutation.mutateAsync(body);
      setResult(response);
    } catch {
      setResult(null);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={mutation.isPending ? () => undefined : onDismiss}
      header="Send scorecard"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss} disabled={mutation.isPending}>
              Close
            </Button>
            <Button variant="primary" onClick={onSubmit} loading={mutation.isPending} disabled={mutation.isPending}>
              Send
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          Send {reportName} by email, text message, or both.
        </Box>

        {errors.form && <Alert type="error">{errors.form}</Alert>}
        {mutation.error && (
          <Alert type="error" header="Send failed">
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Unable to send this scorecard right now. Please try again later.'}
          </Alert>
        )}
        {result && allSent && (
          <Alert type="success" header="Scorecard sent">
            The selected channel completed successfully.
          </Alert>
        )}
        {result && partialFailure && (
          <Alert type="warning" header="Some sends failed">
            Review the failed channel below before retrying.
          </Alert>
        )}
        {result && !partialFailure && !allSent && (
          <Alert type="error" header="Send failed">
            Unable to send this scorecard right now. Please try again later.
          </Alert>
        )}

        <SpaceBetween size="s">
          <FormField label="Email" errorText={errors.email}>
            <Input
              value={email}
              type="email"
              autoComplete="email"
              placeholder="recipient@example.com"
              disabled={mutation.isPending}
              onChange={({ detail }) => {
                setEmail(detail.value);
                if (errors.email || errors.form) setErrors({});
              }}
            />
          </FormField>
          <FormField label="Phone" errorText={errors.phone}>
            <Input
              value={phone}
              autoComplete="tel"
              placeholder="+1 972 793 2587"
              disabled={mutation.isPending}
              onChange={({ detail }) => {
                setPhone(detail.value);
                if (errors.phone || errors.form) setErrors({});
              }}
            />
          </FormField>
        </SpaceBetween>

        {result && (
          <SpaceBetween size="xxs">
            {result.results.flatMap((item) =>
              item.channels.map((channel) => (
                <ResultChannel key={`${item.report_uuid}-${channel.channel}`} channel={channel} />
              )),
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
}
